import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Box } from "@mui/material";
import { useMediaQuery } from "@mui/system";
import { workPath } from "../../utils/workRoutes";

const getItemImageUrl = (item) =>
  item?.ImageUrl ||
  item?.imageUrl ||
  item?.imageURL ||
  item?.image ||
  item?.cover ||
  item?.thumbnail ||
  "";

function WorkCard({ item, to, onOpen }) {
  const open = () => {
    if (to || !onOpen) return;
    onOpen(item);
  };

  return (
    <Box
      className="works-card"
      component={to ? Link : "div"}
      to={to}
      onClick={to ? undefined : open}
      sx={{
        backgroundImage: `url(${getItemImageUrl(item)})`,
      }}
    >
      <div className="works-card-copy">
        <span className="works-card-title">{item.title || "Untitled"}</span>
        {item.description ? (
          <span className="works-card-desc">{item.description}</span>
        ) : null}
      </div>
    </Box>
  );
}

export const TypographyLayout = ({ data, onOpen, category }) => {
  const scrollerRef = useRef(null);
  const applyDeltaRef = useRef(() => {});
  const nudgeAnimRef = useRef(0);
  const isMobile = useMediaQuery("(max-width: 924px)");
  const items = Array.isArray(data) ? data : [];
  const copies = Math.max(2, Math.ceil(8 / Math.max(items.length, 1)));
  const loopedItems =
    !isMobile && items.length
      ? Array.from({ length: copies }, () => items).flat()
      : items;

  const openWork = (item) => {
    if (onOpen) {
      onOpen(item);
      return;
    }
    const href = item?.link;
    if (typeof href !== "string") return;
    const next = href.trim();
    if (!next || !/^https?:\/\//i.test(next)) return;
    window.open(next, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (window.matchMedia("(max-width: 924px)").matches) {
      applyDeltaRef.current = () => {};
      return undefined;
    }

    el.scrollLeft = 0;

    const coarse = window.matchMedia("(pointer: coarse)").matches;
    let dragging = false;
    let lastX = 0;
    let moved = 0;
    let pointerId = null;
    let wrapping = false;
    let clickGuardTimer = 0;

    const loopWidth = () => {
      const cards = el.querySelectorAll(".works-card");
      const n = items.length;
      if (!n || cards.length < n + 1) return 0;
      return cards[n].offsetLeft - cards[0].offsetLeft;
    };

    const wrapScroll = () => {
      const loop = loopWidth();
      if (loop <= 0) return;
      const x = el.scrollLeft;
      if (x < 0 || x >= loop) {
        wrapping = true;
        el.scrollLeft = ((x % loop) + loop) % loop;
        wrapping = false;
      }
    };

    const applyDelta = (dx) => {
      if (!dx) return;
      const loop = loopWidth();
      if (loop <= 0) {
        el.scrollLeft += dx;
        return;
      }
      wrapping = true;
      el.scrollLeft = ((el.scrollLeft + dx) % loop + loop) % loop;
      wrapping = false;
    };

    applyDeltaRef.current = applyDelta;

    const onWheel = (event) => {
      const dx =
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
          ? event.deltaX
          : event.deltaY;
      if (!dx) return;
      event.preventDefault();
      applyDelta(dx * 2.35);
    };

    const onPointerDown = (event) => {
      if (event.pointerType === "touch" || coarse) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      dragging = true;
      lastX = event.clientX;
      moved = 0;
      pointerId = event.pointerId;
    };

    const onPointerMove = (event) => {
      if (!dragging || event.pointerId !== pointerId) return;
      const dx = lastX - event.clientX;
      lastX = event.clientX;
      if (!dx) return;
      moved += Math.abs(dx);
      applyDelta(dx);
      if (moved > 6) {
        if (!el.hasPointerCapture?.(event.pointerId)) {
          el.setPointerCapture?.(event.pointerId);
          el.classList.add("is-dragging");
        }
        event.preventDefault();
      }
    };

    const endDrag = (event) => {
      if (!dragging) return;
      if (event && pointerId != null && event.pointerId !== pointerId) return;
      dragging = false;
      pointerId = null;
      el.classList.remove("is-dragging");
      if (moved > 8) {
        el.dataset.dragged = "1";
        clearTimeout(clickGuardTimer);
        clickGuardTimer = window.setTimeout(() => {
          delete el.dataset.dragged;
        }, 120);
      }
    };

    const onClickCapture = (event) => {
      if (el.dataset.dragged !== "1") return;
      event.preventDefault();
      event.stopPropagation();
      delete el.dataset.dragged;
    };

    const onScroll = () => {
      if (wrapping || coarse) return;
      wrapScroll();
    };

    const onScrollEnd = () => {
      if (wrapping) return;
      wrapScroll();
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove, { passive: false });
    el.addEventListener("pointerup", endDrag);
    el.addEventListener("pointercancel", endDrag);
    el.addEventListener("click", onClickCapture, true);
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("scrollend", onScrollEnd);

    return () => {
      cancelAnimationFrame(nudgeAnimRef.current);
      clearTimeout(clickGuardTimer);
      applyDeltaRef.current = () => {};
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", endDrag);
      el.removeEventListener("pointercancel", endDrag);
      el.removeEventListener("click", onClickCapture, true);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("scrollend", onScrollEnd);
    };
  }, [data, items.length]);

  const nudge = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector(".works-card");
    const track = el.querySelector(".works-track");
    const gap =
      Number.parseFloat(
        getComputedStyle(track || el).columnGap ||
          getComputedStyle(track || el).gap
      ) || 0;
    const step = card
      ? card.getBoundingClientRect().width + gap
      : el.clientWidth * 0.9;
    const target = dir * step;
    const start = performance.now();
    const duration = 320;
    let prev = 0;
    cancelAnimationFrame(nudgeAnimRef.current);
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      applyDeltaRef.current(target * eased - prev);
      prev = target * eased;
      if (t < 1) nudgeAnimRef.current = requestAnimationFrame(tick);
    };
    nudgeAnimRef.current = requestAnimationFrame(tick);
  };

  return (
    <Box className="works-gallery">
      {!isMobile && items.length > 0 && (
        <div className="works-arrows">
          <button
            type="button"
            className="works-nudge works-nudge--left"
            aria-label="Scroll works left"
            onClick={() => nudge(-1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M15 4 7 12l8 8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="square"
              />
            </svg>
          </button>
          <button
            type="button"
            className="works-nudge works-nudge--right"
            aria-label="Scroll works right"
            onClick={() => nudge(1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M9 4l8 8-8 8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="square"
              />
            </svg>
          </button>
        </div>
      )}

      <Box ref={scrollerRef} className="works-scroller">
        <Box className="works-track">
          {loopedItems.map((item, index) => (
            <WorkCard
              key={`${item.id}-${index}`}
              item={item}
              to={category ? workPath(category, item.id) : undefined}
              onOpen={openWork}
            />
          ))}
        </Box>

        <p className="works-band">
          {Array.from({ length: 16 }, () => "TAILORING MUSIC. EVERYDAY. ").join(
            ""
          )}
        </p>
      </Box>
    </Box>
  );
};
